import {
  AccessTokenDataConnectionFragment,
  AccessTokenDataFragment,
  GetAccessTokenListQueryVariables,
  GetAccessTokenQueryVariables,
  Omit,
  useGetAccessTokenListQuery,
  useGetAccessTokenQuery
} from "generated/graphql";
import { useMemo } from "react";
import { parseIntSecondsTimeMilliseconds } from "utilities/datetime";
import * as Urql from "polygraphql";
import * as t from "generated/graphql.schema";

export type AccessToken = {
  recordId: string;
  tenantId: string;
  cursor: any;
  token: string;
  creationTime: number;
  lastModifiedTime: number;
  lastUsedTime: number;
};

export type AccessTokenEdge = {
  cursor: string;
  node: AccessToken;
};

export type AccessTokenConnection = {
  edges: Array<AccessTokenEdge>;
  pageInfo: Pick<t.PageInfo, "hasNextPage" | "endCursor">;
  totalCount: number;
};

export const toAccessToken = (
  accessTokenData: AccessTokenDataFragment,
  cursor: string
): AccessToken => {
  return {
    creationTime: parseIntSecondsTimeMilliseconds(accessTokenData.creationTime),
    cursor: cursor,
    lastModifiedTime: parseIntSecondsTimeMilliseconds(
      accessTokenData.lastModifiedTime
    ),
    lastUsedTime: parseIntSecondsTimeMilliseconds(accessTokenData.lastUsedTime),
    recordId: accessTokenData.recordId,
    tenantId: accessTokenData.tenantId,
    token: accessTokenData.token
  };
};

export const toAccessTokenConnection = (
  connection: AccessTokenDataConnectionFragment
): AccessTokenConnection => {
  const edges: Array<AccessTokenEdge> = connection.edges.map(edge => {
    return {
      node: toAccessToken(edge.node, edge.cursor),
      cursor: edge.cursor
    };
  });

  return {
    totalCount: connection.totalCount,
    pageInfo: connection.pageInfo,
    edges: edges
  };
};

export type GetAccessTokenListOptions = Omit<
  Urql.UseQueryArgs<GetAccessTokenListQueryVariables>,
  "query"
>;

export const useGetAccessTokenList = (
  options: GetAccessTokenListOptions = {}
) => {
  const pause = !options.variables?.tenantId && (options?.pause ?? false);

  const queryResults = useGetAccessTokenListQuery({
    ...options,
    pause
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.searchAccessTokens;
    if (sourceData) {
      result = toAccessTokenConnection(sourceData);
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};

export type GetAccessTokenOptions = Omit<
  Urql.UseQueryArgs<GetAccessTokenQueryVariables & { tenantId: string }>,
  "query"
>;

export const useGetAccessToken = (options: GetAccessTokenOptions = {}) => {
  const pause = !options.variables?.tenantId && (options?.pause ?? false);

  const queryResults = useGetAccessTokenQuery({
    ...options,
    pause
  });

  const data = useMemo(() => {
    let result;
    const sourceData = queryResults.data?.getAccessToken;
    if (sourceData) {
      result = toAccessToken(sourceData, "");
    }
    return result;
  }, [queryResults.data]);

  return { ...queryResults, data };
};
